import { DisplayNutrient } from '@cf-shared/data-model/display-nutrient';
import { TranslatedString } from './translated-string';

/**
 * A nutrient prepared for display, with its text already translated.
 *
 * `DisplayNutrient` in @cf-shared is generic over its text type, because shared code cannot name
 * the client-only `TranslatedString` brand. This alias pins that type parameter for the client,
 * so a component annotated `UiDisplayNutrient[]` is guaranteed to be holding translated names --
 * and binding one into a `TranslatedString` input needs no escape hatch (#4368).
 *
 * This lives in its own tiny module rather than in `util/formatting` so that the interface files
 * that only need the TYPE (for example `cs-day-items-interface.ts`) do not have to import the
 * whole formatting module to get it.
 */
export type UiDisplayNutrient = DisplayNutrient<TranslatedString>;

/**
 * Reads the nutrient list cached on a `FoodHeader`.
 *
 * `FoodHeader` lives in @cf-shared, so its `displayNutrients` field is typed with the default
 * `DisplayNutrient<string>` -- shared cannot name the brand. In practice the field is only ever
 * written by the client's own `FormatNutrients` (checked: every writer in client/src does, apart
 * from two empty-array placeholders), which fills the names from the translation catalog. So the
 * values ARE translated; the field's type just cannot say so.
 *
 * Asserting that once, here, keeps the guarantee in a single documented place instead of letting
 * each reader cast on its own. If `FoodHeader` ever gains a writer that is not `FormatNutrients`,
 * this is the assumption that breaks -- so add new writers through `FormatNutrients` too.
 */
export function uiDisplayNutrients(cached: DisplayNutrient[] | undefined): UiDisplayNutrient[] {
  return (cached ?? []) as UiDisplayNutrient[];
}
